import '@forest/native-diagnostics'

import './lib/posthog'
import { getChinaAndroidTrialProductId } from '@forest/paywall-core'
import {
  getProducts,
  getReceiptSystemAccountIdSafe,
  initializeNativeBridge,
  native,
  pageVisibleOnce,
  type Products,
  type UserInfo,
} from '@forest/native-services'
import { Howler } from 'howler'
import { defineCustomClientStrategy } from './paraglide/runtime'
import { appVersion, debugInitialTheme, language, platform } from './params'
import { api } from './services/api'
import { $receiptSystemAccountId } from './store/receipt-system-account-id'
import { setChinaAndroidTrialState } from './store/china-android-trial'
import { $pageContext } from './store/page-context'
import { setProducts } from './store/products'
import { userInfoSource } from './store/user-info'

defineCustomClientStrategy('custom-queryParam', {
  getLocale: () => language,
  setLocale: () => {},
})

const CHINA_ANDROID_TRIAL_PRODUCT_ID = getChinaAndroidTrialProductId(
  import.meta.env.MODE,
)

async function updateChinaAndroidTrialState(userInfo: UserInfo) {
  if (platform !== 'china_android') {
    setChinaAndroidTrialState('unavailable')
    return
  }

  if (!CHINA_ANDROID_TRIAL_PRODUCT_ID) {
    setChinaAndroidTrialState('unavailable')
    return
  }

  if (import.meta.env.DEV) {
    setChinaAndroidTrialState('available')
    return
  }

  try {
    const result = await api.service
      .get(
        `store/users/${userInfo.user_id}/product/${CHINA_ANDROID_TRIAL_PRODUCT_ID}/own-purchase-stats`,
      )
      .json<{
        stats: {
          month_count: number
          today_count: number
          total_count: number
        }
      }>()

    setChinaAndroidTrialState(
      result.stats.total_count > 0 ? 'unavailable' : 'available',
    )
  } catch (error) {
    console.error('Failed to fetch China Android trial stats', error)
    native.general.close()
  }
}

initializeNativeBridge({
  bifrost: { enableDebug: import.meta.env.MODE !== 'production' },
  onPageVisible: () => {
    pageVisibleOnce.done()
    native.general.logEvent({
      event_name: 'page_view',
      event_params: { page_name: 'relax_paywall' },
    })

    if (import.meta.env.DEV) {
      $pageContext.set({ initial_theme: debugInitialTheme })
    } else {
      native.additional
        .getInitialThemeId()
        .then((id) => $pageContext.set({ initial_theme: id }))
    }
  },
})

void getReceiptSystemAccountIdSafe(appVersion).then((id) => {
  $receiptSystemAccountId.set(id)
})

Howler.autoUnlock = false

const userInfoPromise = userInfoSource.getReady()
const productsPromise = getProducts({
  cacheKeyPrefix: 'relaxPaywall.products.v2',
  api,
  appVersion,
  platform,
  deferredUserInfo: userInfoPromise,
  env: import.meta.env.MODE as 'production' | 'staging' | 'dev',
  shouldMock: import.meta.env.DEV,
})

function productIdsOf(products: Products): string {
  return [
    products.annualOriginal.product_id,
    products.annualEarlyBird.product_id,
    products.monthlyOriginal.product_id,
    products.monthlyEarlyBird.product_id,
  ]
    .concat(products.monthlyNoTrial ? [products.monthlyNoTrial.product_id] : [])
    .sort((a, b) => a - b)
    .join(',')
}

userInfoPromise
  .then(async (userInfo) => {
    void updateChinaAndroidTrialState(userInfo)

    try {
      const products = await productsPromise
      setProducts(products)
      native.general.logEvent({
        event_name: 'plus_price_test_config',
        event_params: { product_ids: productIdsOf(products) },
      })
    } catch {
      console.log('not able to load products')
      native.general.close()
    }
  })
  .catch(() => {
    native.general.close()
  })
